import { useState, useRef, useEffect } from "react";
import { PhoneIcon, EnvelopeIcon, CheckIcon } from "@heroicons/react/24/outline";
import { IMultiSiteOwnerContact } from "../../interfaces/multiSiteOwner";

interface KeyContactsTableProps {
    contacts: IMultiSiteOwnerContact[];
}

export default function KeyContactsTable({ contacts }: KeyContactsTableProps) {
    const [phonePopupId, setPhonePopupId] = useState<string | null>(null);
    const [emailPopupId, setEmailPopupId] = useState<string | null>(null);
    const [copied, setCopied] = useState<string | null>(null);
    const phoneRef = useRef<HTMLDivElement>(null);
    const emailRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (phoneRef.current && !phoneRef.current.contains(event.target as Node)) {
                setPhonePopupId(null);
            }
            if (emailRef.current && !emailRef.current.contains(event.target as Node)) {
                setEmailPopupId(null);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const copyToClipboard = (text: string, type: string) => {
        navigator.clipboard.writeText(text);
        setCopied(`${type}-${text}`);
        setTimeout(() => setCopied(null), 2000);
    };

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Fonction
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Coordonnées
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {contacts.map((contact, idx) => (
                        <tr key={idx} className="hover:bg-gray-50 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{contact.name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">{contact.position}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                    {/* Phone Button */}
                                    <div className="relative" ref={phoneRef}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setPhonePopupId(phonePopupId === `${idx}` ? null : `${idx}`);
                                                setEmailPopupId(null);
                                            }}
                                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                        >
                                            <PhoneIcon className="h-4 w-4" />
                                        </button>

                                        {phonePopupId === `${idx}` && (
                                            <div className="absolute z-10 left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[200px]">
                                                <p className="text-xs text-gray-500 mb-1">Téléphone</p>
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="text-sm font-medium text-gray-900">{contact.phone}</p>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            copyToClipboard(contact.phone, "phone");
                                                        }}
                                                        className="p-1 hover:bg-gray-100 rounded transition-colors"
                                                    >
                                                        {copied === `phone-${contact.phone}` ? (
                                                            <CheckIcon className="h-4 w-4 text-green-600" />
                                                        ) : (
                                                            <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Email Button */}
                                    <div className="relative" ref={emailRef}>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setEmailPopupId(emailPopupId === `${idx}` ? null : `${idx}`);
                                                setPhonePopupId(null);
                                            }}
                                            className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                        >
                                            <EnvelopeIcon className="h-4 w-4" />
                                        </button>

                                        {emailPopupId === `${idx}` && (
                                            <div className="absolute z-10 left-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-3 min-w-[250px]">
                                                <p className="text-xs text-gray-500 mb-1">Email</p>
                                                <div className="flex items-center justify-between gap-2">
                                                    <p className="text-sm font-medium text-gray-900 truncate">{contact.email}</p>
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            copyToClipboard(contact.email, "email");
                                                        }}
                                                        className="p-1 hover:bg-gray-100 rounded transition-colors flex-shrink-0"
                                                    >
                                                        {copied === `email-${contact.email}` ? (
                                                            <CheckIcon className="h-4 w-4 text-green-600" />
                                                        ) : (
                                                            <svg className="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                                            </svg>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
